/**
 * Onboarding Hook
 * @implements US-021 - Automatic Conversation Initiation
 * @implements TS-088 - Onboarding flow restrictions (first project)
 * @implements FS-002.7.1 - Automatic Conversation Initiation (onboarding behavior)
 *
 * This hook manages the onboarding state for first-time users:
 * - Tracks whether the user needs to complete onboarding
 * - Blocks in-app navigation until one exchange is completed
 * - Marks onboarding complete after the first exchange
 *
 * One exchange is defined as: PO sends a message AND receives an AI PM response
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useBlocker, type BlockerFunction } from 'react-router-dom';
import { useMutation, gql } from '@apollo/client';
import { useAuthContext } from '@/providers';

/**
 * GraphQL mutation for completing onboarding
 * @implements TS-088 - Onboarding flow restrictions
 */
const COMPLETE_ONBOARDING = gql`
  mutation CompleteOnboarding {
    completeOnboarding {
      id
      onboardingComplete
    }
  }
`;

/**
 * Input for the useOnboarding hook
 */
export interface UseOnboardingInput {
  /** Current project ID (used to determine if we're in onboarding context) */
  projectId: string;
}

/**
 * Result type for useOnboarding hook
 */
export interface UseOnboardingResult {
  /** Whether the user is currently in onboarding mode */
  isOnboarding: boolean;
  /** Whether one exchange has been completed */
  hasCompletedExchange: boolean;
  /** Whether a message has been sent (first part of exchange) */
  hasSentMessage: boolean;
  /** Whether an AI PM response has been received (second part of exchange) */
  hasReceivedResponse: boolean;
  /** Mark that the user has sent a message */
  markMessageSent: () => void;
  /** Mark that the AI PM has responded */
  markResponseReceived: () => void;
  /** Whether navigation is currently blocked */
  isNavigationBlocked: boolean;
  /** Manually complete onboarding (if needed) */
  completeOnboarding: () => Promise<void>;
  /** Whether completing onboarding is in progress */
  isCompletingOnboarding: boolean;
}

/**
 * useOnboarding Hook
 * @implements US-021 - Automatic Conversation Initiation
 * @implements TS-088 - Onboarding flow restrictions
 *
 * Manages the onboarding experience for first-time Product Owners.
 * Blocks in-app navigation until they complete one exchange with the AI PM.
 *
 * @example
 * ```tsx
 * const {
 *   isOnboarding,
 *   hasCompletedExchange,
 *   markMessageSent,
 *   markResponseReceived,
 * } = useOnboarding({ projectId: 'proj-123' });
 *
 * // In message send handler:
 * await sendMessage(content);
 * markMessageSent();
 *
 * // In subscription handler for AI PM response:
 * if (newMessage.senderType === 'AI_PM') {
 *   markResponseReceived();
 * }
 * ```
 */
export const useOnboarding = ({
  projectId,
}: UseOnboardingInput): UseOnboardingResult => {
  const { user, refetch } = useAuthContext();

  // Track exchange progress locally
  const [hasSentMessage, setHasSentMessage] = useState(false);
  const [hasReceivedResponse, setHasReceivedResponse] = useState(false);

  // Mutation to complete onboarding
  const [completeOnboardingMutation, { loading: isCompletingOnboarding }] =
    useMutation(COMPLETE_ONBOARDING);

  /**
   * Determine if user is in onboarding mode
   * @implements FS-002.7.1 - First project onboarding behavior
   *
   * User is in onboarding if:
   * 1. User exists
   * 2. onboardingComplete is false (or undefined for backwards compatibility)
   * 3. We have a project ID (we're viewing a project)
   */
  const isOnboarding = useMemo(() => {
    if (!user) return false;
    if (!projectId) return false;

    // Check if user has completed onboarding
    // onboardingComplete might not exist on older user objects
    const onboardingComplete = (user as { onboardingComplete?: boolean })
      .onboardingComplete;

    return onboardingComplete === false;
  }, [user, projectId]);

  /**
   * One exchange = sent message + received response
   */
  const hasCompletedExchange = hasSentMessage && hasReceivedResponse;

  /**
   * Mark that the user has sent a message
   */
  const markMessageSent = useCallback(() => {
    setHasSentMessage(true);
  }, []);

  /**
   * Mark that the AI PM has responded and complete onboarding if needed
   * @implements TS-088 - Onboarding flow restrictions
   */
  const markResponseReceived = useCallback(() => {
    setHasReceivedResponse(true);
  }, []);

  /**
   * Complete onboarding by calling the mutation and refetching user
   */
  const completeOnboarding = useCallback(async () => {
    try {
      await completeOnboardingMutation();
      // Refetch user to update auth state with onboardingComplete: true
      await refetch();
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      // Still allow navigation even if mutation fails
    }
  }, [completeOnboardingMutation, refetch]);

  /**
   * Automatically complete onboarding when exchange is completed
   * @implements FS-002.7.1 - After one exchange, onboarding is marked complete
   */
  useEffect(() => {
    if (isOnboarding && hasCompletedExchange) {
      completeOnboarding();
    }
  }, [isOnboarding, hasCompletedExchange, completeOnboarding]);

  /**
   * Navigation blocker using React Router's useBlocker
   * @implements FS-002.7.1 - Navigation blocking applies to in-app navigation only
   *
   * Blocks navigation when:
   * 1. User is in onboarding mode
   * 2. Exchange has not been completed yet
   *
   * NOTE: Browser-level navigation (close tab, back to external site) is NOT blocked
   * per the spec. React Router's blocker only handles in-app navigation.
   */
  const blockerFunction: BlockerFunction = useCallback(
    ({ nextLocation }) => {
      // Only block if in onboarding and exchange not completed
      if (!isOnboarding || hasCompletedExchange) {
        return false;
      }

      // Allow navigation to the same project (e.g., refreshing)
      if (nextLocation.pathname.includes(`/projects/${projectId}`)) {
        return false;
      }

      // Block all other in-app navigation during onboarding
      return true;
    },
    [isOnboarding, hasCompletedExchange, projectId],
  );

  const blocker = useBlocker(blockerFunction);

  /**
   * Handle blocked navigation
   * When navigation is blocked, we don't redirect - we just prevent the navigation
   * The UI will show a banner/message indicating why navigation is blocked
   */
  useEffect(() => {
    if (blocker.state === 'blocked') {
      // Log for debugging
      console.debug(
        '[useOnboarding] Navigation blocked: Complete one exchange with the AI PM first',
      );
    }
  }, [blocker.state]);

  return {
    isOnboarding,
    hasCompletedExchange,
    hasSentMessage,
    hasReceivedResponse,
    markMessageSent,
    markResponseReceived,
    isNavigationBlocked: blocker.state === 'blocked',
    completeOnboarding,
    isCompletingOnboarding,
  };
};
